import React from 'react';
import NoMachinesEmptyState from 'app/components/access-sharing/EquipmentAndFleets/NoMachinesEmptyState';
import { LayoutContext } from 'app/components/ReactContexts/layoutContext';
import {
  AccessSharingStepController,
  EquipmentsSharingOverviewStepController,
} from './components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AccessSharingFlowProvider,
  useAccessSharingFlowContext,
} from 'app/hooks/access-sharing';
import { ACCESS_SHARING_INDIVIDUAL_EQUIPMENT_STEPS } from './constants';
import useNavigationPrompt from 'app/hooks/useNavigationPrompt';
import { ConfirmNavigateAwayDialog } from 'app/components/dialogs';
import { useIsMobile } from 'app/hooks/useIsMobile';
import { EquipmentListContext } from 'app/components/ReactContexts/equipmentListContext';

export default function ShareIndividualEquipmentPage() {
  const { id } = useParams();

  const { setClassNames, removeClassNames } = React.useContext(LayoutContext);
  const isMobile = useIsMobile();

  // TODO throw it away as soon as possible
  // Refactor layout components in the future to be more flexible
  // and avoid hacks like this.
  React.useEffect(() => {
    let classNameIndex = -1;

    if (!isMobile) {
      classNameIndex = setClassNames('layout-grey');
    }
    return () => {
      if (classNameIndex >= 0) {
        removeClassNames(classNameIndex);
        classNameIndex = -1;
      }
    };
  }, [isMobile]);

  // TODO Request a design
  if (!id) {
    return <h1>Equipment Not Found</h1>;
  }

  return (
    <AccessSharingFlowProvider
      initialEquipmentIds={new Set([id])}
      stepCount={Object.keys(ACCESS_SHARING_INDIVIDUAL_EQUIPMENT_STEPS).length}
      // startStep={ACCESS_SHARING_EQUIPMENT_STEPS.OVERVIEW}
    >
      <StepRenderer id={id} />
      <UnsavedChangesWarning />
    </AccessSharingFlowProvider>
  );
}

function StepRenderer({ id }: { id: string }) {
  const navigate = useNavigate();
  const { stepper } = useAccessSharingFlowContext();
  const { step } = stepper;

  const {
    equipmentListData: { equipmentList },
  } = React.useContext(EquipmentListContext);

  function handleBack() {
    navigate(`/equipment/${id}`);
  }

  if (!equipmentList?.length) {
    return <NoMachinesEmptyState onClose={() => handleBack()} />;
  }

  switch (step) {
    case ACCESS_SHARING_INDIVIDUAL_EQUIPMENT_STEPS.ACCESS_SHARING:
      return <AccessSharingStepController onBack={handleBack} />;
    case ACCESS_SHARING_INDIVIDUAL_EQUIPMENT_STEPS.OVERVIEW:
      return (
        <EquipmentsSharingOverviewStepController
          equipment={equipmentList}
          onBack={handleBack}
        />
      );
    default:
      return null;
  }
}

function UnsavedChangesWarning() {
  const {
    isSubmitted,
    isEditing,
    selectedUsers,
    selectedEquipmentIds,
    stepper,
  } = useAccessSharingFlowContext();
  const { step } = stepper;

  const isDirty = selectedUsers.size > 0 || selectedEquipmentIds.size > 0;
  const isStepWithPrompt =
    step === ACCESS_SHARING_INDIVIDUAL_EQUIPMENT_STEPS.OVERVIEW;

  const { showPrompt, confirmNavigation, cancelNavigation } =
    useNavigationPrompt(
      isDirty && (isStepWithPrompt || isEditing) && !isSubmitted,
    );

  return (
    <ConfirmNavigateAwayDialog
      open={showPrompt}
      onConfirm={confirmNavigation}
      onClose={cancelNavigation}
    />
  );
}
